import React from 'react'
import { Card, Grid, Group, Rating, Sx, Text, useMantineTheme } from '@mantine/core'
import Actions from 'components/business/Campaign/List/Actions'
import { Campaign, CampaignType, CampaignWidgetType, FeedbackStats } from 'types/domain'
import { formatSlashedISODATE } from 'utils'
import { StatusIndicator } from 'components/business'
import { getTextColor } from 'config/themeOverride/getColor'
import { IconAlignBoxCenterMiddle, IconFileDescription, IconLayoutNavbarExpand } from '@tabler/icons-react'
import { RowData } from 'components/ui/ScrollableTable'
import DeliveryUrl from '../../DeliveryUrl'
import DistributionReview from '../../DistributionReview'
import useTranslation from 'next-translate/useTranslation'

type Props = {
  data: any
  onItemClick: (campaign: Campaign) => void
  itemStyle?: (data: RowData) => Sx
}

const GridBody = ({ data, onItemClick, itemStyle }: Props) => {
  const theme = useMantineTheme()
  const { t } = useTranslation('componentCampaignList')

  const getTag = (campaign: Campaign) => {
    const color = getTextColor('gray.6', theme)
    if (campaign.type === CampaignType.Feedback) {
      return <Rating value={(campaign.stats as FeedbackStats).score ?? 0} />
    }
    if (campaign.type === CampaignType.Survey) {
      return <IconFileDescription size={14} color={color} />
    }
    if (campaign.type === CampaignType.Notification) {
      if (campaign.widget.type === CampaignWidgetType.Banner) {
        return (
          <Group spacing="xs">
            <IconLayoutNavbarExpand size={14} color={color} />
            <Text c={color} size="xs">
              {t(`list.widgetType.compact.${CampaignWidgetType.Banner}`)}
            </Text>
          </Group>
        )
      }
      if (campaign.widget.type === CampaignWidgetType.Popup) {
        return (
          <Group spacing="xs">
            <IconAlignBoxCenterMiddle size={14} color={getTextColor('gray.6', theme)} />
            <Text c={getTextColor('gray.6', theme)} size="xs">
              {t(`list.widgetType.compact.${CampaignWidgetType.Popup}`)}
            </Text>
          </Group>
        )
      }
    }
    return <></>
  }

  return (
    <Grid columns={25}>
      {data.map((i: any, idx: number) => (
        <Grid.Col span={5} key={idx}>
          <Card
            withBorder
            radius="md"
            className="group"
            onClick={() => onItemClick(i.rawData)}
            sx={itemStyle?.(i.rawData)}
          >
            <Card.Section inheritPadding py="xs">
              <Group position="apart">
                {getTag(i.rawData)}
                <StatusIndicator enabled={i.rawData.enabled} />
              </Group>
            </Card.Section>
            <Card.Section inheritPadding>
              <Text h={40} lineClamp={2}>
                {i.rawData.name}
              </Text>
            </Card.Section>
            <Card.Section inheritPadding py="xs">
              <Group spacing={0}>
                <Text size="lg">{i.rawData.entriesCount ?? 0}</Text>
                <Text c={getTextColor('gray.6', theme)} className="ml-2" size="xs">
                  {t(`list.entriesCountSuffix`)}
                </Text>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="xs">
              <Group position="apart">
                <Text c={getTextColor('gray.6', theme)}>{formatSlashedISODATE(i.rawData.createdAt)}</Text>
                <Group spacing="0">
                  {i.rawData.widget?.type !== CampaignWidgetType.Banner && (
                    <DeliveryUrl id={i.rawData.id} compact={true} showText={false} />
                  )}
                  <DistributionReview id={i.rawData.id} compact={true} showText={false} />
                  <Actions campaign={i.rawData} onRefresh={i.refetch} onItemChange={i.onItemChange} />
                </Group>
              </Group>
            </Card.Section>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  )
}

export default GridBody
